import React from "react";
import { Link } from "react-router-dom";
import { Mail, Globe, MapPin, Phone, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import OpeningHours from "@/components/OpeningHours";
import { LicensingMicrotext } from "@/components/LicensingBlock";

export default function Footer({ settings }) {
  const c = settings?.contact || {};
  const licensing = settings?.licensing;
  const year = new Date().getFullYear();

  const hasField = (v) => v && String(v).trim().length > 0;

  return (
    <footer data-testid="site-footer" className="relative bg-navy text-white overflow-hidden">
      <div className="img-grain absolute inset-0" />
      <div className="container relative pt-20 pb-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Logo variant="white" />
            <p className="mt-6 max-w-md text-sm leading-relaxed text-white/70">
              Integrated healthcare in Somalia and the region - medical care, advanced diagnostics
              and pharmacy & wellness through one trusted network.
            </p>
            <div className="mt-8 flex flex-col gap-2 text-sm text-white/80">
              {hasField(c.email) && (
                <a className="inline-flex items-center gap-2.5 hover:text-teal-light transition-colors" href={`mailto:${c.email}`} data-testid="footer-email">
                  <Mail className="h-4 w-4 text-teal-light" /> {c.email}
                </a>
              )}
              {hasField(c.phone) && (
                <a className="inline-flex items-center gap-2.5 hover:text-teal-light transition-colors" href={`tel:${(c.phone || "").replace(/[\s\-()]/g, "")}`} data-testid="footer-phone">
                  <Phone className="h-4 w-4 text-teal-light" /> {c.phone}
                </a>
              )}
              {hasField(c.website) && (
                <a className="inline-flex items-center gap-2.5 hover:text-teal-light transition-colors" href={`https://${c.website}`} target="_blank" rel="noreferrer" data-testid="footer-website">
                  <Globe className="h-4 w-4 text-teal-light" /> {c.website}
                </a>
              )}
              {(hasField(c.address_line_1) || hasField(c.city)) && (
                <span className="inline-flex items-center gap-2.5">
                  <MapPin className="h-4 w-4 text-teal-light" />
                  {[c.address_line_1, c.address_line_2, c.city, c.country].filter(hasField).join(", ")}
                </span>
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.22em] text-teal-light font-semibold">Divisions</h4>
            <ul className="mt-5 space-y-3 text-sm">
              <li><Link to="/divisions/medical-center" className="hover:text-teal-light transition-colors">Infinity Medical Center</Link></li>
              <li><Link to="/divisions/medical-lab" className="hover:text-teal-light transition-colors">Infinity Medical Lab</Link></li>
              <li><Link to="/divisions/pharmacy-wellness" className="hover:text-teal-light transition-colors">Infinity Pharmacy & Wellness</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.22em] text-teal-light font-semibold">Company</h4>
            <ul className="mt-5 space-y-3 text-sm">
              <li><Link to="/about" className="hover:text-teal-light transition-colors">About</Link></li>
              <li><Link to="/services" className="hover:text-teal-light transition-colors">Services</Link></li>
              <li><Link to="/team" className="hover:text-teal-light transition-colors" data-testid="footer-team">Team</Link></li>
              <li><Link to="/why-choose-us" className="hover:text-teal-light transition-colors">Why IMG</Link></li>
              <li><Link to="/contact" className="hover:text-teal-light transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.22em] text-teal-light font-semibold">Resources</h4>
            <ul className="mt-5 space-y-3 text-sm">
              <li><Link to="/privacy" className="hover:text-teal-light transition-colors">Privacy</Link></li>
              <li><Link to="/terms" className="hover:text-teal-light transition-colors">Terms</Link></li>
              <li><Link to="/cookie-policy" className="hover:text-teal-light transition-colors" data-testid="footer-cookie-policy">Cookie Policy</Link></li>
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent("img-open-cookie-prefs"))}
                  data-testid="footer-cookie-prefs"
                  className="hover:text-teal-light transition-colors text-left"
                >
                  Cookie preferences
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12">
          <div className="grid gap-6 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <OpeningHours hours={c.opening_hours} variant="footer" />
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <LicensingMicrotext licensing={licensing} />
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col gap-3 md:flex-row md:items-center md:justify-between text-xs text-white/55">
          <p>&copy; {year} Infinity Medical Group. All rights reserved.</p>
          <p className="font-medium tracking-[0.18em] uppercase text-teal-light">
            Care &middot; Compassion &middot; Commitment
          </p>
        </div>
      </div>
    </footer>
  );
}
